/**
 * 올린 HTML 의 기준 크기를 잰다(2026-10-20) — 만든 사람에게 해상도를 묻지 않고 콘솔이 잰다(`@signageone/core` 의 `html-fit.ts`).
 *
 * 서버가 내주는 미리보기 주소에 `measure=1` 을 붙이면 재기 스크립트를 끼운 문서가 온다. 그것을 크기가 다른 두 창에 띄우면
 * 스크립트가 내용이 차지한 상자를 부모(콘솔)에게 알려 준다 — 출처 없는 샌드박스 안이라 숫자를 보내는 것밖에 못 한다. 둘을
 * 견주어 고정 크기인지 늘고 주는 HTML 인지 가르고(`classifyHtmlLayout`), 서버에 적는다(`html_size`).
 *
 * 창은 화면에 띄우되 보이지 않게(거의 투명·아주 작게) 둔다 — 화면 밖에 두면 브라우저가 그 창의 그리기를 멈춰, 스크립트로
 * 그리는 배너가 자리를 잡지 못한다.
 */
import { useEffect, useRef } from 'react';
import {
  classifyHtmlLayout,
  HTML_MEASURE_MESSAGE,
  HTML_MEASURE_VIEWPORTS,
  HTML_SANDBOX,
  type HtmlLayout,
  type HtmlMeasure,
} from '@signageone/core';
import type { Asset, SignageOneClient } from '../api/client.js';
import { bestZoom, COARSE_ZOOMS, fineZooms, fitsWhole, zoomedSize, type Size } from './htmlScreens.js';

/** 서버가 경로로 준 주소를 서버 기준으로 — 콘솔이 다른 출처에서 떠도 서버로 간다. */
export function htmlFrameSrc(url: string, base = (globalThis as { __SIGNAGEONE_BASE__?: string }).__SIGNAGEONE_BASE__): string {
  if (!url.startsWith('/') || url.startsWith('//') || !base) return url;
  return base.replace(/\/+$/, '') + url;
}

/** 한 창을 기다리는 한도 — 재기는 다 읽고 1.6초 뒤에 끝난다. 무거운 문서를 생각해 넉넉히. */
const MEASURE_TIMEOUT_MS = 10_000;

const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);

/** 재기 스크립트가 보낸 말 — 모양이 맞지 않으면 null(문서 안의 스크립트가 아무 말이나 보낼 수 있다). */
type Box = NonNullable<HtmlMeasure['box']>;

/** 상자 하나 — 모양이 맞지 않으면 undefined(그 말 전체를 버린다), null 이면 그릴 것이 없다. */
function parseBox(value: unknown): Box | null | undefined {
  if (value === null) return null;
  const box = value as { left?: unknown; top?: unknown; right?: unknown; bottom?: unknown } | undefined;
  if (!box || !finite(box.left) || !finite(box.top) || !finite(box.right) || !finite(box.bottom)) return undefined;
  return { left: box.left, top: box.top, right: box.right, bottom: box.bottom };
}

export function parseMeasure(data: unknown): HtmlMeasure | null {
  if (!data || typeof data !== 'object') return null;
  const message = data as { type?: unknown; vw?: unknown; vh?: unknown; box?: unknown; full?: unknown; near?: unknown };
  if (message.type !== HTML_MEASURE_MESSAGE || !finite(message.vw) || !finite(message.vh)) return null;
  const box = parseBox(message.box);
  if (box === undefined) return null;
  const measure: HtmlMeasure = { vw: message.vw, vh: message.vh, box };
  // 끝까지 잰 상자(2026-10-20) — 옛 스크립트는 싣지 않는다. 모양이 틀리면 그것만 버린다(가르기에는 `box` 로 넉넉하다).
  if ('full' in message) {
    const full = parseBox(message.full);
    if (full !== undefined) measure.full = full;
  }
  if (message.near === null || finite(message.near)) measure.near = message.near;
  return measure;
}

function measureIn(src: string, viewport: { width: number; height: number }): Promise<HtmlMeasure | null> {
  return new Promise((resolve) => {
    const frame = document.createElement('iframe');
    frame.setAttribute('sandbox', HTML_SANDBOX);
    frame.setAttribute('aria-hidden', 'true');
    frame.tabIndex = -1;
    frame.referrerPolicy = 'no-referrer';
    frame.className = 'html-measure-frame';
    frame.style.width = `${viewport.width}px`;
    frame.style.height = `${viewport.height}px`;
    let settled = false;
    const finish = (value: HtmlMeasure | null): void => {
      if (settled) return;
      settled = true;
      window.removeEventListener('message', onMessage);
      window.clearTimeout(timer);
      frame.remove();
      resolve(value);
    };
    const onMessage = (event: MessageEvent): void => {
      // 이 창이 보낸 마지막 말만 — 다른 창·다른 문서의 말은 듣지 않는다.
      if (event.source !== frame.contentWindow) return;
      if (!(event.data as { final?: unknown } | null)?.final) return;
      finish(parseMeasure(event.data));
    };
    window.addEventListener('message', onMessage);
    const timer = window.setTimeout(() => finish(null), MEASURE_TIMEOUT_MS);
    frame.src = src;
    document.body.appendChild(frame);
  });
}

/** 재기 스크립트를 끼운 주소. */
function measureSrc(previewUrl: string): string {
  return `${htmlFrameSrc(previewUrl)}${previewUrl.includes('?') ? '&' : '?'}measure=1`;
}

/** 한 크기의 창에서 잰다 — 잘리는가를 볼 때(`contentCut`). 재지 못하면 null. */
export function measureContent(previewUrl: string, viewport: { width: number; height: number }): Promise<HtmlMeasure | null> {
  return measureIn(measureSrc(previewUrl), viewport);
}

/**
 * 잘리지 않는 가장 큰 크기(%)를 찾는다 — [화면에 다 맞추기](2026-10-20).
 *
 * 사람이 보이는 크기를 한 칸씩 내려 가며 미리보기를 들여다보던 일을 콘솔이 한다. 그 화면 비율의 창을 크게부터 몇 개 한꺼번에 재고
 * (`COARSE_ZOOMS`), 들어가는 것과 잘리는 것 사이를 5% 씩 다시 잰다. 크기를 줄일수록(창이 커질수록) 들어가기 쉽다고 본다. 하나도
 * 들어가지 않으면 null, 잴 수 없었으면 undefined.
 */
export async function findWholeFitZoom(previewUrl: string, screen: Size): Promise<number | null | undefined> {
  const measureZoom = async (zoom: number): Promise<{ zoom: number; fits: boolean | null }> => {
    const measure = await measureContent(previewUrl, zoomedSize(screen, { fit: 'auto', size: null }, zoom));
    return { zoom, fits: measure && measure.full !== undefined ? fitsWhole(measure) : null };
  };
  const coarse = await Promise.all(COARSE_ZOOMS.map(measureZoom));
  if (coarse.every((result) => result.fits === null)) return undefined;
  const best = bestZoom(coarse);
  if (best === null) return null;
  const above = coarse.filter((result) => result.zoom > best && result.fits === false).map((result) => result.zoom);
  if (above.length === 0) return best;
  const fine = await Promise.all(fineZooms(best, Math.min(...above)).map(measureZoom));
  return bestZoom([...coarse, ...fine]);
}

/** 미리보기 주소(`preview_url`)의 문서를 두 창에서 재어 가른다. 재지 못하면 null. */
export async function measureHtmlLayout(previewUrl: string): Promise<HtmlLayout | null> {
  const src = measureSrc(previewUrl);
  const [a, b] = await Promise.all(HTML_MEASURE_VIEWPORTS.map((viewport) => measureIn(src, viewport)));
  if (!a || !b) return null;
  return classifyHtmlLayout(a, b);
}

/** 잰 결과를 서버에 적는다 — 고정 크기면 그 크기, 늘고 주는 HTML 이면 null. */
export function layoutPatch(layout: HtmlLayout): { html_size: { width: number; height: number } | null; html_size_manual: false } {
  return {
    html_size: layout.kind === 'fixed' ? { width: layout.width, height: layout.height } : null,
    html_size_manual: false,
  };
}

/** 이 세션에서 이미 재 본 콘텐츠 — 재지 못한 것을 목록이 다시 그려질 때마다 거듭 재지 않게. */
const tried = new Set<string>();

/**
 * 아직 재지 않은 올린 HTML 을 저절로 잰다 — 올린 직후·목록·상세에서. 하나씩 차례로(창 두 개씩) 재고, 적으면 `onMeasured`.
 * 고칠 수 없는 사람(`enabled` false)에게는 재지 않는다 — 적을 수 없다.
 */
export function useHtmlAutoMeasure(
  client: SignageOneClient,
  assets: readonly Asset[] | undefined,
  enabled: boolean,
  onMeasured: () => void,
): void {
  const busy = useRef(false);
  const latest = useRef(onMeasured);
  latest.current = onMeasured;
  const pending = (assets ?? []).filter(
    (asset) => asset.type === 'html' && asset.html_measured_at === null && asset.preview_url && !tried.has(asset.id),
  );
  const key = pending.map((asset) => asset.id).join(',');

  useEffect(() => {
    if (!enabled || busy.current || pending.length === 0) return;
    busy.current = true;
    void (async () => {
      let wrote = false;
      for (const asset of pending) {
        tried.add(asset.id);
        try {
          const layout = await measureHtmlLayout(asset.preview_url as string);
          if (!layout) continue;
          await client.updateAsset(asset.id, layoutPatch(layout));
          wrote = true;
        } catch {
          /* 재지 못한 것은 그대로 둔다 — 화면은 예전처럼 화면 크기 그대로 띄운다 */
        }
      }
      busy.current = false;
      if (wrote) latest.current();
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 재지 않은 것의 목록이 바뀔 때만
  }, [enabled, key]);
}
